import { roll2D6 } from '../utils/dice.js';
import { getYear } from '../utils/time.js';
import { calculateTaskTotal } from './taskSystem.js';

/**
 * Resolves a raid against the settlement
 * @param {Object} params - Parameters object
 * @param {number} params.day - Current game day
 * @param {Object} params.rolls - Task roll results
 * @param {Object} params.pushes - Push results
 * @param {Array} params.buildings - Completed buildings
 * @param {Object} params.resources - Current resources { food, wood, stone }
 * @param {Array} params.workers - Current workers
 * @param {Object|null} params.event - Current event (for surprise flag and weather/golden_age)
 * @param {string|null} params.bountyTask - Bounty task (guard bounty is now a real defensive option)
 * @returns {Object} Combat result with raid strength, defense, outcome, and updated resources/workers
 */
export function resolveRaid({ day, rolls = {}, pushes = {}, buildings = [], resources, workers, event = null, bountyTask = null }) {
  const year = getYear(day);
  const isSurprise = event?.surprise === true;
  const raidStrength = roll2D6() + (year * 3) + (isSurprise ? 2 : 0);

  // Guard defense uses the full task system (building bonuses, weather, golden_age, bounty)
  let guardTotal = 0;
  if (rolls.guard && rolls.guard.rolls.length > 0) {
    guardTotal = calculateTaskTotal({
      task: 'guard',
      rollData: rolls.guard,
      pushes,
      buildings,
      event,
      bountyTask
    });
  }

  const wallBonus = buildings.some(b => b.type === 'wall' && b.complete) ? 3 : 0;
  const fortressBonus = buildings.some(b => b.type === 'fortress' && b.complete) ? 5 : 0;
  const defense = guardTotal + wallBonus + fortressBonus;

  const won = defense >= raidStrength;
  const damage = won ? 0 : raidStrength - defense;
  const surplus = won ? defense - raidStrength : 0;

  const { losses, newResources, newWorkers } = won
    ? {
        losses: { food: 0, wood: 0, stone: 0, workersInjured: 0, workersKilled: 0 },
        newResources: resources,
        newWorkers: workers
      }
    : distributeDamage(damage, resources, workers);

  return {
    raidStrength,
    defense,
    won,
    damage,
    surplus,
    surprise: isSurprise,
    losses,
    resources: newResources,
    workers: newWorkers
  };
}

/**
 * Distributes raid damage across resources and workers
 * @param {number} damage - Total damage to distribute
 * @param {Object} resources - Current resources
 * @param {Array} workers - Current workers
 * @returns {Object} { losses, newResources, newWorkers }
 */
function distributeDamage(damage, resources, workers) {
  let remaining = damage;
  const losses = { food: 0, wood: 0, stone: 0, workersInjured: 0, workersKilled: 0 };
  let newFood = resources.food;
  let newWood = resources.wood;
  let newStone = resources.stone;
  let newWorkers = [...workers];

  // Deduct from food first
  const foodLoss = Math.min(remaining, newFood);
  newFood -= foodLoss;
  remaining -= foodLoss;
  losses.food = foodLoss;

  // Then wood
  if (remaining > 0) {
    const woodLoss = Math.min(remaining, newWood);
    newWood -= woodLoss;
    remaining -= woodLoss;
    losses.wood = woodLoss;
  }

  // Then stone
  if (remaining > 0) {
    const stoneLoss = Math.min(remaining, newStone);
    newStone -= stoneLoss;
    remaining -= stoneLoss;
    losses.stone = stoneLoss;
  }

  // Overflow damage injures/kills workers — 1 casualty per 4 excess damage, max 3
  if (remaining > 0) {
    const workerCasualties = Math.min(3, Math.ceil(remaining / 4));

    for (let i = 0; i < workerCasualties; i++) {
      // Prefer injuring healthy workers; injure → dead cascade
      const healthyWorkers = newWorkers.filter(w => w.status === 'healthy');
      if (healthyWorkers.length > 0) {
        const target = healthyWorkers[Math.floor(Math.random() * healthyWorkers.length)];
        newWorkers = newWorkers.map(w => w.id === target.id ? { ...w, status: 'injured' } : w);
        losses.workersInjured++;
      } else {
        const injuredWorkers = newWorkers.filter(w => w.status === 'injured');
        if (injuredWorkers.length > 0) {
          const target = injuredWorkers[Math.floor(Math.random() * injuredWorkers.length)];
          newWorkers = newWorkers.map(w => w.id === target.id ? { ...w, status: 'dead' } : w);
          losses.workersKilled++;
        }
      }
    }
  }

  return {
    losses,
    newResources: { food: newFood, wood: newWood, stone: newStone },
    newWorkers
  };
}
